import React from 'react';
import { 
  ShoppingCart, 
  Package, 
  DollarSign, 
  TrendingUp,
  TrendingDown,
  Activity,
  Users,
  Building,
  Heart,
  AlertTriangle,
  CheckCircle,
  Clock,
  Target,
  BarChart3,
  PieChart as PieChartIcon,
  Calendar,
  Zap
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import StatCard from '../../components/StatCard';

const ExecutiveDashboard: React.FC = () => {
  const { user } = useAuth();

  // Executive KPI Data
  const executiveKPIs = {
    totalRevenue: 2847500,
    revenueGrowth: 12.5,
    totalOrders: 15847,
    orderGrowth: 8.2,
    totalPatients: 14650,
    patientGrowth: 5.8,
    grossMargin: 42.3,
    marginChange: 2.1,
    complianceScore: 98.7,
    complianceChange: 0.3,
    inventoryTurnover: 8.4,
    turnoverChange: 1.2
  };

  // Revenue by Customer Type
  const revenueByCustomerType = [
    { type: 'Aged Care', revenue: 1420000, percentage: 49.9, color: '#8B5CF6' },
    { type: 'NDIS', revenue: 680000, percentage: 23.9, color: '#10B981' },
    { type: 'Retail', revenue: 520000, percentage: 18.3, color: '#F59E0B' },
    { type: 'Hospital', revenue: 227500, percentage: 8.0, color: '#EF4444' }
  ];

  // Monthly Performance Trends
  const monthlyPerformance = [
    { month: 'Jan', revenue: 235000, orders: 1245, patients: 1180, margin: 41.2 },
    { month: 'Feb', revenue: 218000, orders: 1156, patients: 1165, margin: 40.8 },
    { month: 'Mar', revenue: 267000, orders: 1389, patients: 1220, margin: 42.1 },
    { month: 'Apr', revenue: 289000, orders: 1456, patients: 1285, margin: 43.2 },
    { month: 'May', revenue: 245000, orders: 1298, patients: 1245, margin: 41.9 },
    { month: 'Jun', revenue: 312000, orders: 1567, patients: 1340, margin: 44.1 },
    { month: 'Jul', revenue: 298000, orders: 1489, patients: 1320, margin: 43.5 },
    { month: 'Aug', revenue: 276000, orders: 1378, patients: 1295, margin: 42.8 },
    { month: 'Sep', revenue: 285000, orders: 1423, patients: 1315, margin: 43.0 }
  ];

  // Top Performing Categories
  const topCategories = [
    { category: 'Cardiovascular', revenue: 485000, volume: 3250, margin: 45.2 },
    { category: 'Endocrine', revenue: 398000, volume: 2890, margin: 38.7 },
    { category: 'Mental Health', revenue: 287000, volume: 1980, margin: 41.3 },
    { category: 'Respiratory', revenue: 245000, volume: 1750, margin: 39.8 },
    { category: 'Gastrointestinal', revenue: 198000, volume: 1450, margin: 42.1 }
  ];

  // Operational Metrics
  const operationalMetrics = [
    { metric: 'Order Fulfillment Rate', value: 98.5, target: 98.0, status: 'above' },
    { metric: 'Average Order Processing Time', value: 2.3, target: 3.0, status: 'above', unit: 'hours' },
    { metric: 'Stock Accuracy', value: 99.2, target: 99.0, status: 'above' },
    { metric: 'Customer Satisfaction', value: 4.6, target: 4.5, status: 'above', unit: '/5.0' },
    { metric: 'Medication Error Rate', value: 0.02, target: 0.05, status: 'above', unit: '%' },
    { metric: 'Compliance Score', value: 98.7, target: 95.0, status: 'above' }
  ];

  // Critical Alerts Summary
  const criticalAlerts = [
    { type: 'Stock', count: 12, severity: 'critical', description: 'Critical stock levels requiring immediate attention' },
    { type: 'Expiry', count: 8, severity: 'high', description: 'Medications expiring within 7 days' },
    { type: 'Compliance', count: 3, severity: 'medium', description: 'Regulatory compliance items pending' },
    { type: 'Orders', count: 5, severity: 'high', description: 'Orders requiring pharmacist review' }
  ];


  return (
    <div className="space-y-8">
      {/* Executive Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
            Executive Dashboard
          </h1>
          <p className="text-gray-600 mt-2">Real-time business intelligence and key performance indicators for {user?.branch}</p>
        </div>
        <div className="text-right">
          <p className="text-sm text-gray-500">Last updated</p>
          <p className="text-sm font-medium text-gray-900">{new Date().toLocaleString()}</p>
        </div>
      </div>

      {/* Executive KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <StatCard
          title="Total Revenue"
          value={`$${(executiveKPIs.totalRevenue / 1000000).toFixed(2)}M`}
          subtitle="Annual revenue"
          icon={<DollarSign className="h-6 w-6" />}
          trend="vs last year"
          trendValue={executiveKPIs.revenueGrowth}
          color="text-green-600"
          bgGradient="bg-gradient-to-br from-green-500 to-green-600"
        />
        <StatCard
          title="Total Orders"
          value={executiveKPIs.totalOrders.toLocaleString()}
          subtitle="This year"
          icon={<ShoppingCart className="h-6 w-6" />}
          trend="vs last year"
          trendValue={executiveKPIs.orderGrowth}
          color="text-blue-600"
          bgGradient="bg-gradient-to-br from-blue-500 to-blue-600"
        />
        <StatCard
          title="Active Patients"
          value={executiveKPIs.totalPatients.toLocaleString()}
          subtitle="Registered patients"
          icon={<Users className="h-6 w-6" />}
          trend="vs last year"
          trendValue={executiveKPIs.patientGrowth}
          color="text-purple-600"
          bgGradient="bg-gradient-to-br from-purple-500 to-purple-600"
        />
        <StatCard
          title="Gross Margin"
          value={`${executiveKPIs.grossMargin}%`}
          subtitle="Overall profitability"
          icon={<TrendingUp className="h-6 w-6" />}
          trend="vs last quarter"
          trendValue={executiveKPIs.marginChange}
          color="text-orange-600"
          bgGradient="bg-gradient-to-br from-orange-500 to-orange-600"
        />
        <StatCard
          title="Compliance Score"
          value={`${executiveKPIs.complianceScore}%`}
          subtitle="Regulatory compliance"
          icon={<CheckCircle className="h-6 w-6" />}
          trend="vs last quarter"
          trendValue={executiveKPIs.complianceChange}
          color="text-emerald-600"
          bgGradient="bg-gradient-to-br from-emerald-500 to-emerald-600"
        />
        <StatCard
          title="Inventory Turnover"
          value={`${executiveKPIs.inventoryTurnover}x`}
          subtitle="Annual turnover rate"
          icon={<Activity className="h-6 w-6" />}
          trend="vs last year"
          trendValue={executiveKPIs.turnoverChange}
          color="text-indigo-600"
          bgGradient="bg-gradient-to-br from-indigo-500 to-indigo-600"
        />
      </div>

      {/* Critical Alerts Overview */}
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="h-6 w-6 text-red-600" />
            <div>
              <h3 className="text-xl font-bold text-gray-900">Critical Alerts Overview</h3>
              <p className="text-gray-600 text-sm">Items requiring executive attention</p>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {criticalAlerts.map((alert, index) => (
            <div key={index} className={`group relative overflow-hidden p-6 rounded-2xl border-2 transition-all duration-300 hover:scale-105 hover:shadow-xl ${
              alert.severity === 'critical' ? 'bg-gradient-to-br from-red-50 to-red-100 border-red-300 hover:border-red-400' :
              alert.severity === 'high' ? 'bg-gradient-to-br from-orange-50 to-orange-100 border-orange-300 hover:border-orange-400' :
              'bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-300 hover:border-yellow-400'
            }`}>
              {/* Animated background */}
              <div className={`absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity duration-300 ${
                alert.severity === 'critical' ? 'bg-red-500' :
                alert.severity === 'high' ? 'bg-orange-500' :
                'bg-yellow-500'
              }`} />
              
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-bold text-gray-900 text-lg">{alert.type}</h4>
                  <span className={`px-3 py-1.5 text-sm font-bold rounded-full shadow-lg ${
                    alert.severity === 'critical' ? 'bg-gradient-to-r from-red-500 to-red-600 text-white' :
                    alert.severity === 'high' ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white' :
                    'bg-gradient-to-r from-yellow-500 to-yellow-600 text-white'
                  }`}>
                    {alert.count}
                  </span>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">{alert.description}</p>
                
                {/* Animated indicator */}
                <div className="mt-4 flex items-center space-x-2">
                  <div className={`w-2 h-2 rounded-full animate-pulse ${
                    alert.severity === 'critical' ? 'bg-red-500' :
                    alert.severity === 'high' ? 'bg-orange-500' :
                    'bg-yellow-500'
                  }`} />
                  <span className="text-xs font-medium text-gray-600">
                    {alert.severity === 'critical' ? 'Immediate Action Required' :
                     alert.severity === 'high' ? 'High Priority' : 'Medium Priority'}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Main Analytics Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Revenue by Customer Type */}
        <div className="group bg-white/80 backdrop-blur-md rounded-3xl shadow-xl border border-white/30 p-8 hover:shadow-2xl transition-all duration-500 hover:scale-[1.02]">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h3 className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">Revenue by Customer Type</h3>
              <p className="text-gray-600 text-sm mt-1">Distribution of revenue streams</p>
            </div>
            <div className="p-3 bg-gradient-to-br from-purple-100 to-pink-100 rounded-2xl group-hover:scale-110 transition-transform duration-300">
              <PieChartIcon className="h-6 w-6 text-purple-600" />
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={revenueByCustomerType}
                cx="50%"
                cy="50%"
                outerRadius={100}
                fill="#8884d8"
                dataKey="revenue"
                label={({ type, percentage }) => `${type}: ${percentage}%`}
              >
                {revenueByCustomerType.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => [`$${value.toLocaleString()}`, 'Revenue']} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Monthly Performance Trends */}
        <div className="group bg-white/80 backdrop-blur-md rounded-3xl shadow-xl border border-white/30 p-8 hover:shadow-2xl transition-all duration-500 hover:scale-[1.02]">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h3 className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">Monthly Performance Trends</h3>
              <p className="text-gray-600 text-sm mt-1">Revenue and order trends</p>
            </div>
            <div className="p-3 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl group-hover:scale-110 transition-transform duration-300">
              <BarChart3 className="h-6 w-6 text-blue-600" />
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={monthlyPerformance}>
              <defs>
                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" axisLine={false} tickLine={false} />
              <YAxis axisLine={false} tickLine={false} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'white', 
                  border: 'none', 
                  borderRadius: '12px', 
                  boxShadow: '0 10px 25px rgba(0,0,0,0.1)' 
                }} 
                formatter={(value, name) => [
                  name === 'revenue' ? `$${value.toLocaleString()}` : value,
                  name === 'revenue' ? 'Revenue' : name === 'orders' ? 'Orders' : 'Patients'
                ]}
              />
              <Area 
                type="monotone" 
                dataKey="revenue" 
                stroke="#8B5CF6" 
                strokeWidth={3}
                fillOpacity={1} 
                fill="url(#colorRevenue)" 
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Operational Excellence Metrics */}
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Operational Excellence Metrics</h3>
            <p className="text-gray-600 text-sm">Key performance indicators vs targets</p>
          </div>
          <Target className="h-5 w-5 text-gray-600" />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {operationalMetrics.map((metric, index) => (
            <div key={index} className="p-4 bg-gray-50 rounded-xl">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-gray-900">{metric.metric}</h4>
                {metric.status === 'above' ? (
                  <CheckCircle className="h-5 w-5 text-green-600" />
                ) : (
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                )}
              </div>
              
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Current:</span>
                  <span className={`text-sm font-medium ${metric.status === 'above' ? 'text-green-600' : 'text-red-600'}`}>
                    {metric.value}{metric.unit || '%'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Target:</span>
                  <span className="text-sm font-medium text-gray-900">
                    {metric.target}{metric.unit || '%'}
                  </span>
                </div>
                
                <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                  <div 
                    className={`h-2 rounded-full ${metric.status === 'above' ? 'bg-green-500' : 'bg-red-500'}`}
                    style={{ 
                      width: `${Math.min((metric.value / (metric.target * 1.2)) * 100, 100)}%` 
                    }}
                  ></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Top Performing Categories */}
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Top Performing Categories</h3>
            <p className="text-gray-600 text-sm">Revenue and margin performance by therapeutic category</p>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50/50">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Category</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Revenue</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Volume</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Margin</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Performance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {topCategories.map((category, index) => (
                <tr key={index} className="hover:bg-gray-50/50">
                  <td className="px-6 py-4 font-medium text-gray-900">{category.category}</td>
                  <td className="px-6 py-4 font-medium text-gray-900">${category.revenue.toLocaleString()}</td>
                  <td className="px-6 py-4 text-gray-900">{category.volume.toLocaleString()}</td>
                  <td className="px-6 py-4">
                    <span className={`font-medium ${category.margin > 40 ? 'text-green-600' : 'text-orange-600'}`}>
                      {category.margin}%
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <div className="w-16 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-purple-500 h-2 rounded-full" 
                          style={{ width: `${(category.margin / 50) * 100}%` }}
                        ></div>
                      </div>
                      <TrendingUp className="h-4 w-4 text-green-500" />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ExecutiveDashboard;
